import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchNebulaConversationDetail,
  fetchNebulaConversations,
} from '../../../../services/api/nebula-insights.service';
import type {
  NebulaConversationDetail,
  NebulaConversationSummary,
  NebulaDaysFilter,
} from '../../../../types/nebula-insights';
import ConversationsFilters from './ConversationsFilters';
import ConversationsTable from './ConversationsTable';
import ConversationDetailDrawer from './ConversationDetailDrawer';

interface ConversationsTabProps {
  days: NebulaDaysFilter;
  refreshToken: number;
  selectedConversationId: string | null;
  onSelectedConversationHandled: () => void;
  onRequestOpenConversation: (conversationId: string) => void;
}

const ConversationsTab: React.FC<ConversationsTabProps> = ({
  days,
  refreshToken,
  selectedConversationId,
  onSelectedConversationHandled,
  onRequestOpenConversation,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [model, setModel] = useState('');
  const [page, setPage] = useState(1);
  const [conversations, setConversations] = useState<NebulaConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [activeConversation, setActiveConversation] = useState<NebulaConversationDetail | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, model, days]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchNebulaConversations({
          page: days === 'recent' ? 1 : page,
          limit: days === 'recent' ? 7 : 20,
          search: debouncedSearch,
          model,
          days,
        });
        if (!cancelled) {
          setConversations(result.conversations);
          setTotal(result.pagination.total);
          setTotalPages(result.pagination.totalPages);
        }
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.message || 'Failed to load conversations');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, model, days, refreshToken]);

  const modelOptions = useMemo(() => {
    const values = new Set<string>();
    conversations.forEach((item) => {
      if (item.model) values.add(item.model);
    });
    return Array.from(values).sort();
  }, [conversations]);

  const openConversation = useCallback(async (conversationId: string) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setActiveConversation(null);

    try {
      const detail = await fetchNebulaConversationDetail(conversationId);
      setActiveConversation(detail);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load conversation detail');
      setDrawerOpen(false);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedConversationId) return;
    openConversation(selectedConversationId);
    onSelectedConversationHandled();
  }, [selectedConversationId, openConversation, onSelectedConversationHandled]);

  return (
    <>
      <div className="space-y-4">
        <ConversationsFilters
          search={search}
          model={model}
          onSearchChange={setSearch}
          onModelChange={setModel}
          modelOptions={modelOptions}
        />
        <ConversationsTable
          conversations={conversations}
          loading={loading}
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          onOpenConversation={(id) => {
            onRequestOpenConversation(id);
            openConversation(id);
          }}
        />
      </div>

      <ConversationDetailDrawer
        open={drawerOpen}
        loading={drawerLoading}
        conversation={activeConversation}
        onClose={() => {
          setDrawerOpen(false);
          setActiveConversation(null);
        }}
      />
    </>
  );
};

export default ConversationsTab;
